import classNames from 'classnames'
import {
  forwardRef,
  KeyboardEvent,
  KeyboardEventHandler,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createEditor, Node, Text } from 'slate'
import { withHistory } from 'slate-history'
import {
  Editable,
  RenderElementProps,
  RenderLeafProps,
  RenderPlaceholderProps,
  Slate,
  withReact,
} from 'slate-react'

import { EBackgroundColors } from 'core-components/interfaces'
import { Label } from 'core-components/library/inputs'

import { LoaderOverlay } from '../loader'

import { DEFAULT_EDITOR_VALUE, DEFAULT_SELECTION_VALUE } from './constants'
import { Element, Leaf } from './customStyleView'
import { emptyContent } from './helpers/emptyContentHelper'
import { hotKeyHandler } from './helpers/hotKeyHelper'
import { parseHtmlToValue, serialize } from './helpers/htmlHelper'
import { isSelectionAction } from './helpers/slateHelper'
import { CustomEditor } from './interface/custom-types'
import { IRichTextEditorProps } from './interface/IRichTextEditorProps'
import { withCorrectVoidBehavior, withExpandBlock, withImages, withInlines } from './plugins'
import { Toolbar } from './toolbar/Toolbar'

import styles from './RichTextEditor.module.scss'
import './styles.scss'

export const RichTextEditor = forwardRef(
  (
    {
      classes,
      margin,
      value,
      onChange,
      onInputFinished,
      fileUploadAdapter,
      onFileDownload,
      onFileRemove,
      label,
      toolbarVisible = true,
      placeholder,
      disabled = false,
      autoFocus = false,
      toolbarButtonsView,
      onKeyDown,
      extendedBlocks,
      backgroundColor = EBackgroundColors.white,
      minLinesCount = 5,
      borderColor = 'default',
      toolbarCustomButtons,
      fontSizes,
    }: IRichTextEditorProps,
    ref
  ) => {
    const editableRef = useRef<HTMLDivElement>(null)
    const [htmlValue, setHtmlValue] = useState<string>('')
    const renderElement = useCallback(
      (props: RenderElementProps) => <Element {...props} extendedBlocks={extendedBlocks} />,
      [extendedBlocks]
    )
    const [focused, setFocused] = useState<boolean>(false)
    const renderLeaf = useCallback((props: RenderLeafProps) => <Leaf {...props} />, [])
    const [isLoading, setIsLoading] = useState(false)

    const withExtendedBlocks = useCallback(
      (editor: CustomEditor) => {
        if (extendedBlocks) {
          return extendedBlocks.reduce((acc, block) => {
            if (block.setupExtendedBlock) {
              return block.setupExtendedBlock(acc)
            }
            return acc
          }, editor)
        }

        return editor
      },
      [extendedBlocks]
    )

    const onLoad = (isLoading: boolean) => {
      setIsLoading(isLoading)
    }

    const editor = useMemo(
      () =>
        withExtendedBlocks(
          withExpandBlock(
            withInlines(
              withCorrectVoidBehavior(withImages(withHistory(withReact(createEditor())), onLoad))
            )
          )
        ),
      [withExtendedBlocks]
    )

    const heightClassName = useMemo(() => {
      return styles[`minCountLines-${minLinesCount || 5}`]
    }, [minLinesCount])

    const renderPlaceholder = useCallback(({ children, attributes }: RenderPlaceholderProps) => {
      const attributesWithoutStyles = { ...attributes, style: {} }
      return (
        <span
          {...attributesWithoutStyles}
          data-testid={'rich-text-editor-placeholder'}
          className={styles.editableArea__placeholder}
          tabIndex={1}
        >
          {children}
        </span>
      )
    }, [])

    const onKeyDownHandler: KeyboardEventHandler<HTMLInputElement> = useCallback(
      (event: KeyboardEvent) => {
        if (onKeyDown) {
          onKeyDown(event)
        }
        if (!event.isDefaultPrevented()) {
          return hotKeyHandler(editor, event)
        }
        return event.preventDefault()
      },
      [editor, onKeyDown]
    )

    const saveChanges = useCallback(() => {
      if (onInputFinished) {
        onInputFinished(htmlValue)
      }
    }, [onInputFinished, htmlValue])

    useImperativeHandle(ref, () => ({ editor, editableRef: editableRef.current }))

    useEffect(() => {
      editor.fileUploadAdapter = fileUploadAdapter
      editor.onFileDownload = onFileDownload
      editor.onFileRemove = onFileRemove
    }, [editor, fileUploadAdapter, onFileDownload, onFileRemove])

    useEffect(() => {
      editor.disabled = disabled
    }, [disabled, editor])

    useEffect(() => {
      //Prop value in slate - it's initial value for render
      setHtmlValue((prev) => {
        //It's trigger, when content changed from outside
        if (prev !== value) {
          const text = value ? parseHtmlToValue({ value, extendedBlocks }) : DEFAULT_EDITOR_VALUE
          // If value is empty from outside we have to move selection to default position
          if (text === DEFAULT_EDITOR_VALUE) {
            editor.selection = DEFAULT_SELECTION_VALUE
          }
          // Setting new value from outside to editor
          editor.children = text
          // Triggering this event for changing editor value from outside https://docs.slatejs.org/walkthroughs/06-saving-to-a-database
          editor.onChange()
          //checking if a new set of nodes comes into the editor and the selection
          // points to a node from the old set
          if (editor.selection) {
            const path = editor.selection.anchor.path
            try {
              const node = Node.get(editor, path)
              //checking if it`s a non-leaf node
              if (!Text.isText(node)) {
                editor.selection = null
              }
            } catch (error) {
              editor.selection = null
            }
          }

          return value
        }

        return prev
      })
    }, [editor, value])

    return (
      <div
        className={classNames(styles.root, classes?.root, {
          [`margin-bottom-${margin}`]: !!margin,
        })}
        onBlur={saveChanges}
        data-testid={'rich-text-editor-root'}
      >
        <LoaderOverlay show={isLoading} />
        <Slate
          editor={editor}
          initialValue={DEFAULT_EDITOR_VALUE}
          onChange={(value) => {
            if (isSelectionAction(editor)) {
              const content = value.map((item) => serialize(item, extendedBlocks)).join('')
              const finalContent = emptyContent(content)
              setHtmlValue(finalContent)
              if (onChange) {
                onChange(finalContent)
              }
            }
          }}
        >
          {toolbarVisible ? (
            <div
              className={classNames(styles.toolBar, classes?.toolbar, {
                [styles.toolBarDisabled]: disabled,
              })}
              data-testid={'rich-text-editor-toolbar'}
            >
              <Toolbar
                toolbarButtonsView={toolbarButtonsView}
                toolbarCustomButtons={toolbarCustomButtons}
                disabled={disabled}
                fontSizes={fontSizes}
              />
            </div>
          ) : null}

          {!disabled ? (
            <div className={styles.labelContainer}>
              <Label
                __testId={'rich-text-editor-label'}
                label={label}
                value={htmlValue}
                focused={focused}
                labelClassName={styles.label}
              />
            </div>
          ) : null}
          <div
            ref={editableRef}
            className={classNames({
              [styles['richEditorRoot--editMode']]: !disabled,
              [styles[`border--${borderColor}--focused`]]: focused,
              [styles[`border--${borderColor}`]]: !disabled && !focused,
              [heightClassName]: !disabled,
              [`content-background-${backgroundColor}`]: !disabled,
              [styles.richEditorRoot]: true,
            })}
          >
            <Editable
              data-testid={'rich-text-editor'}
              renderElement={renderElement}
              onBlur={() => setFocused(false)}
              onFocus={() => setFocused(true)}
              renderLeaf={renderLeaf}
              placeholder={label ? undefined : placeholder}
              spellCheck
              autoFocus={autoFocus}
              readOnly={disabled}
              onKeyDown={onKeyDownHandler}
              className={classNames({
                [styles.editableArea]: true,
                [styles.editableArea__enabled]: !disabled,
                [styles.editableArea__disabled]: disabled,
              })}
              renderPlaceholder={renderPlaceholder}
            />
          </div>
        </Slate>
      </div>
    )
  }
)

RichTextEditor.displayName = 'RichTextEditor'
